import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { NAV, SITE } from "../data/site";

const WhatsAppIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
);

export { WhatsAppIcon };

const Header = () => {
    const [open, setOpen] = useState(false);
    const [sectorsOpen, setSectorsOpen] = useState(false);
    const { pathname } = useLocation();

    const isActive = (to) => to === "/" ? pathname === "/" : pathname.startsWith(to);

    return (
        <header className="fixed top-0 inset-x-0 z-50" data-testid="site-header">
            <div className="bg-white/90 backdrop-blur-xl border-b border-slate-200/80 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 lg:h-[72px]">
                    <Link to="/" className="flex items-center gap-2.5 group" data-testid="header-logo">
                        <picture>
                            <source srcSet="/assets/om-infra-logo.webp" type="image/webp" />
                            <img src="/assets/om-infra-logo.jpg" alt="OM Infra logo" title="OM Infra — YEIDA Plot Advisory" width="40" height="40" className="w-10 h-10 rounded-sm object-cover" />
                        </picture>
                        <span className="leading-tight">
                            <span className="block font-serif font-bold text-navy text-lg tracking-tight">OM Infra</span>
                            <span className="block text-[10px] uppercase tracking-[0.22em] text-gold-deep font-mono">YEIDA Plot Advisory</span>
                        </span>
                    </Link>

                    <nav className="hidden xl:flex items-center gap-1" data-testid="desktop-nav">
                        {NAV.map((item) =>
                            item.children ? (
                                <div key={item.label} className="relative group">
                                    <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-700 hover:text-navy transition-colors" data-testid="nav-sectors-dropdown">
                                        {item.label} <ChevronDown className="w-3.5 h-3.5" />
                                    </button>
                                    <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                                        <div className="bg-white rounded-lg shadow-xl border border-slate-100 py-2 w-44">
                                            {item.children.map((c) => (
                                                <Link key={c.to} to={c.to} className="block px-4 py-2 text-sm text-slate-700 hover:bg-bone hover:text-navy" data-testid={`nav-${c.label.toLowerCase().replace(" ", "-")}`}>
                                                    {c.label}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    className={`px-3 py-2 text-sm font-medium transition-colors ${isActive(item.to) ? "text-gold-deep" : "text-slate-700 hover:text-navy"}`}
                                >
                                    {item.label}
                                </NavLink>
                            )
                        )}
                    </nav>

                    <div className="flex items-center gap-2">
                        <a
                            href={SITE.phoneHref}
                            data-testid="header-call-button"
                            className="hidden sm:inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2.5 rounded-sm hover:bg-navy-800 transition-colors"
                        >
                            <Phone className="w-4 h-4" /> Call Now
                        </a>
                        <a
                            href={SITE.whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-whatsapp-button"
                            className="inline-flex items-center gap-2 bg-[#25D366] text-white text-sm font-semibold px-3 sm:px-4 py-2.5 rounded-sm hover:brightness-95 transition-all"
                        >
                            <WhatsAppIcon className="w-4 h-4" /> <span className="hidden sm:inline">WhatsApp</span>
                        </a>
                        <button
                            className="xl:hidden p-2 text-navy"
                            onClick={() => setOpen(!open)}
                            aria-label="Toggle menu"
                            data-testid="mobile-menu-toggle"
                        >
                            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="xl:hidden bg-white border-b border-slate-200 shadow-lg overflow-hidden"
                        data-testid="mobile-menu"
                    >
                        <nav className="px-4 py-3 max-h-[70vh] overflow-y-auto">
                            {NAV.map((item) =>
                                item.children ? (
                                    <div key={item.label}>
                                        <button
                                            className="w-full flex items-center justify-between py-3 text-sm font-semibold text-navy border-b border-slate-100"
                                            onClick={() => setSectorsOpen(!sectorsOpen)}
                                            data-testid="mobile-nav-sectors-toggle"
                                        >
                                            {item.label}
                                            <ChevronDown className={`w-4 h-4 transition-transform ${sectorsOpen ? "rotate-180" : ""}`} />
                                        </button>
                                        {sectorsOpen &&
                                            item.children.map((c) => (
                                                <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="block pl-4 py-2.5 text-sm text-slate-600 border-b border-slate-50" data-testid={`mobile-nav-${c.label.toLowerCase().replace(" ", "-")}`}>
                                                    {c.label}
                                                </Link>
                                            ))}
                                    </div>
                                ) : (
                                    <Link
                                        key={item.to}
                                        to={item.to}
                                        onClick={() => setOpen(false)}
                                        className="block py-3 text-sm font-semibold text-navy border-b border-slate-100"
                                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    >
                                        {item.label}
                                    </Link>
                                )
                            )}
                            <a href={SITE.phoneHref} className="mt-3 flex items-center justify-center gap-2 bg-navy text-white font-semibold py-3 rounded-sm" data-testid="mobile-call-button">
                                <Phone className="w-4 h-4" /> Call {SITE.phone}
                            </a>
                            <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold py-3 rounded-sm" data-testid="mobile-whatsapp-button">
                                <WhatsAppIcon className="w-4 h-4" /> WhatsApp Us
                            </a>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
